import React, { useState, useCallback } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
} from '@mellify/ui';
import { PlatformIcon } from './PlatformIcon';
import { Shield, ExternalLink, Eye, EyeOff } from 'lucide-react';
import { useToast } from '../../../../hooks/useToast';
import { useWorkspaceStore } from '../../../../stores';
import { connectHashnode } from '../../../../services/socialAccountCallbackService';
import { useQueryClient } from '@tanstack/react-query';

interface ConnectHashnodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const ConnectHashnodeModal: React.FC<ConnectHashnodeModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [accessToken, setAccessToken] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const toast = useToast();
  const queryClient = useQueryClient();
  const currentWorkspace = useWorkspaceStore((s) => s.currentWorkspace);

  const handleConnect = useCallback(async () => {
    if (!currentWorkspace?.id) {
      toast.error('No Workspace', 'Please select a workspace first.');
      return;
    }

    if (!accessToken.trim()) {
      toast.error('Missing Token', 'Please enter your Hashnode personal access token.');
      return;
    }

    setIsConnecting(true);

    try {
      const result = await connectHashnode({
        workspaceId: currentWorkspace.id,
        accessToken: accessToken.trim(),
      });

      toast.success(
        'Hashnode Connected!',
        `${result.account?.accountName || 'Your Hashnode blog'} has been connected successfully.`
      );

      // Invalidate social accounts queries to refetch
      queryClient.invalidateQueries({ queryKey: ['social-accounts'] });
      queryClient.invalidateQueries({ queryKey: ['social-account-health'] });

      // Reset form
      setAccessToken('');
      setShowToken(false);

      onSuccess();
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to connect your Hashnode account.';

      if (message.includes('Invalid') || message.includes('unauthorized') || message.includes('401')) {
        toast.error(
          'Invalid Token',
          'The personal access token is invalid. Please check your token and try again.'
        );
      } else {
        toast.error('Connection Failed', message);
      }
    } finally {
      setIsConnecting(false);
    }
  }, [currentWorkspace, accessToken, toast, queryClient, onSuccess]);

  const handleClose = useCallback(() => {
    if (!isConnecting) {
      setAccessToken('');
      setShowToken(false);
      onClose();
    }
  }, [isConnecting, onClose]);

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      size="md"
      closeOnOverlayClick={!isConnecting}
      closeOnEscape={!isConnecting}
      aria-labelledby="connect-hashnode-title"
    >
      <ModalHeader>
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-white dark:bg-dark-700 border border-dark-200 dark:border-dark-600">
            <PlatformIcon platform="hashnode" size="md" />
          </div>
          <div>
            <h2
              id="connect-hashnode-title"
              className="text-lg font-semibold text-dark-900 dark:text-dark-100"
            >
              Connect Hashnode
            </h2>
            <p className="text-xs text-dark-500 dark:text-dark-400 mt-0.5">
              Connect using a personal access token
            </p>
          </div>
        </div>
      </ModalHeader>

      <ModalBody>
        {/* Security notice */}
        <div className="flex items-start gap-3 p-3 rounded-lg bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 mb-5">
          <Shield className="w-5 h-5 text-primary-500 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-primary-700 dark:text-primary-300">
              Secure Connection
            </p>
            <p className="text-xs text-primary-600 dark:text-primary-400 mt-0.5">
              Your token is encrypted and stored securely. We never share your credentials.
            </p>
          </div>
        </div>

        {/* Instructions */}
        <div className="mb-5">
          <h3 className="text-sm font-medium text-dark-700 dark:text-dark-300 mb-2">
            How to get your personal access token:
          </h3>
          <ol className="text-xs text-dark-500 dark:text-dark-400 space-y-1.5 list-decimal list-inside">
            <li>
              Go to{' '}
              <a
                href="https://hashnode.com/settings/developer"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-500 hover:text-primary-600 underline inline-flex items-center gap-0.5"
              >
                Hashnode Developer Settings
                <ExternalLink className="w-3 h-3" />
              </a>
            </li>
            <li>Click &quot;Generate New Token&quot; or copy an existing token</li>
            <li>Paste the token below</li>
          </ol>
        </div>

        {/* Token input */}
        <div className="space-y-4">
          <div>
            <label
              htmlFor="hashnode-token"
              className="block text-sm font-medium text-dark-700 dark:text-dark-300 mb-1.5"
            >
              Personal Access Token
            </label>
            <div className="relative">
              <Input
                id="hashnode-token"
                type={showToken ? 'text' : 'password'}
                value={accessToken}
                onChange={(e) => setAccessToken(e.target.value)}
                placeholder="Enter your Hashnode personal access token"
                disabled={isConnecting}
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowToken(!showToken)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-dark-400 hover:text-dark-600 dark:hover:text-dark-300"
                aria-label={showToken ? 'Hide token' : 'Show token'}
              >
                {showToken ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Data usage note */}
        <div className="mt-5 pt-4 border-t border-dark-200 dark:border-dark-700">
          <p className="text-xs text-dark-400 dark:text-dark-500">
            By connecting, you agree to allow Mellify to publish and manage articles on your
            Hashnode blog. You can disconnect this account at any time from the Channels page.
          </p>
        </div>
      </ModalBody>

      <ModalFooter>
        <Button
          variant="outline"
          size="md"
          onClick={handleClose}
          disabled={isConnecting}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          size="md"
          onClick={handleConnect}
          loading={isConnecting}
          disabled={!accessToken.trim()}
        >
          {isConnecting ? 'Connecting...' : 'Connect'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

ConnectHashnodeModal.displayName = 'ConnectHashnodeModal';